import React, { Dispatch, ReactNode } from "react";
import { AioExternalReplacements, AioExternalSingle } from "../additional/interface";
import { defaultEditorDefns, EditorDefns, TLF_VIEW_MODES, TlfViewModeConfig } from "../utility";
import { AitTable } from "./AitTable";
import { AitRowGroupData } from "./functions/AitRowGroupData";
import { AitTableData } from "./functions/interface";
import { TableContextProvider } from "./TableContextProvider";

export interface AsupInternalTableProps<T> {
  id: string;
  tableData: AitTableData<T>;
  isEditable?: boolean;
  onChange: Dispatch<AitTableData<T>>;
  externalLists?: AioExternalReplacements<T>[];
  externalSingles?: AioExternalSingle<T>[];
  style?: React.CSSProperties;
  showCellBorders?: boolean;
  groupTemplates: AitRowGroupData<T>[];
  colWidthMod?: number;
  initialDecimalAlignPercent?: number;
  defaultCellWidth?: number;
  noTableOptions?: boolean;
  noTrafficLights?: boolean;
  editorProps?: EditorDefns<T>;
  tlfViewMode?: TlfViewModeConfig;
}

/**
 * High-level table component that wires table data into the table context
 * provider and renders an `AitTable`.
 *
 * @param props - Component props
 * @param props.id - The table id
 * @param props.tableData - Table model data
 * @param props.isEditable - Whether table is editable
 * @param props.onChange - Callback for pushed changes
 * @param props.externalLists - External replacements lists
 * @param props.externalSingles - External single lists
 * @param props.style - Optional container style
 * @param props.showCellBorders - Toggle cell borders
 * @param props.groupTemplates - Row group templates
 * @param props.colWidthMod - Column width modifier
 * @param props.defaultCellWidth - Default width for cells
 * @param props.noTableOptions - Hide table options when true
 * @param props.noTrafficLights - Hide traffic lights when true
 * @param props.editorProps - Editor props used by cell editors
 * @param props.tlfViewMode - View mode configuration
 */
export const AsupInternalTable = <T,>({
  id,
  tableData,
  isEditable = true,
  onChange,
  externalLists = [],
  externalSingles = [],
  style,
  showCellBorders = true,
  groupTemplates,
  colWidthMod = 2,
  defaultCellWidth = 60,
  noTableOptions = false,
  noTrafficLights = false,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  tlfViewMode = TLF_VIEW_MODES[0],
}: AsupInternalTableProps<T>): ReactNode => (
  <TableContextProvider<T>
    initialState={{
      originalTableData: {
        ...tableData,
        changedRowGroups:
          tableData.changedRowGroups ??
          // istanbul ignore next
          [],
        changedColumns:
          tableData.changedColumns ??
          // istanbul ignore next
          [],
      },
      colWidthMod,
      defaultCellWidth,
      editorProps,
      editable: isEditable,
      externalLists,
      externalSingles,
      groupTemplates,
      id,
      noTableOptions,
      noTrafficLights,
      showCellBorders,
      tlfViewMode,
    }}
    onChange={onChange}
  >
    <AitTable
      id={id}
      style={style}
    />
  </TableContextProvider>
);

AsupInternalTable.displayName = "AsupInternalTable";
